/**
 * P4-1: <RackDiffPreview> — визуальный before/after diff стойки внутри ReviewGate.
 * Реиспользует существующий RackCanvas; сам грузит текущее оборудование стойки.
 */
import { useEffect, useMemo, useState } from 'react'
import { api } from '../../utils/api'
import { RackCanvas } from '../equipment/RackCanvas'
import type { WorkOrderItemPreview } from '../../types/workOrder'
import {
  affectedRackIds,
  buildRackDiff,
  occupiedPositions,
  type RackEquipmentLike,
} from './rackDiff.logic'

const U_SLOTS_DEFAULT = 42

interface Props {
  dcCode: string
  items: WorkOrderItemPreview[]
}

export function RackDiffPreview({ dcCode, items }: Props) {
  const rackIds = useMemo(() => affectedRackIds(items), [items])
  const [rackId, setRackId] = useState<number | null>(rackIds[0] ?? null)
  const [tab, setTab] = useState<'before' | 'after'>('after')
  const [current, setCurrent] = useState<RackEquipmentLike[] | null>(null)

  useEffect(() => {
    if (rackId === null && rackIds.length > 0) setRackId(rackIds[0])
  }, [rackIds, rackId])

  useEffect(() => {
    if (rackId === null) return
    let alive = true
    api.get(`/api/datacenters/${dcCode}/racks/${rackId}/equipment`)
      .then(res => { if (alive) setCurrent(Array.isArray(res.data) ? res.data : []) })
      .catch(() => { if (alive) setCurrent([]) })
    return () => { alive = false }
  }, [dcCode, rackId])

  if (rackIds.length === 0 || rackId === null) return null
  if (current === null) {
    return <div className="text-xs text-text-secondary px-1 py-2">Загрузка стойки…</div>
  }

  const diff = buildRackDiff(current, items, rackId)
  const list = tab === 'before' ? diff.before : diff.after

  return (
    <div className="mx-6 mt-4 rounded border border-border bg-bg-surface p-3" data-testid="rack-diff-preview">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-text-primary">Стойка #{rackId}</span>
          {rackIds.length > 1 && (
            <select
              value={rackId}
              onChange={e => { setRackId(Number(e.target.value)); setCurrent(null) }}
              className="bg-bg-panel border border-border rounded text-xs text-text-primary px-1 py-0.5"
              aria-label="Выбор стойки для diff"
            >
              {rackIds.map(id => <option key={id} value={id}>#{id}</option>)}
            </select>
          )}
          <span className="text-[11px] text-text-secondary">
            {diff.addedCount > 0 && <span className="text-green-400">+{diff.addedCount} </span>}
            {diff.removedCount > 0 && <span className="text-red-400">−{diff.removedCount}</span>}
          </span>
        </div>
        <div className="flex rounded border border-border overflow-hidden" role="tablist">
          {(['before', 'after'] as const).map(t => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={`px-3 py-1 text-xs transition-colors ${
                tab === t ? 'bg-blue-600 text-white' : 'bg-bg-panel text-text-secondary hover:text-text-primary'
              }`}
            >
              {t === 'before' ? 'До' : 'После'}
            </button>
          ))}
        </div>
      </div>
      <div className="max-h-64 overflow-y-auto">
        <RackCanvas
          uSlots={U_SLOTS_DEFAULT}
          occupiedPositions={occupiedPositions(list, U_SLOTS_DEFAULT)}
          equipmentList={list as never[]}
          maxWeightKg={0}
          currentWeight={0}
        />
      </div>
    </div>
  )
}
